/**
 * DeadWeightSkillEffect — Grit grapple-path thrown-body attack.
 *
 * Throw a currently grappled enemy up to 3 tiles in any direction.
 * Wall impacts deal bonus damage to the thrown target. Collisions
 * with other enemies deal bonus damage to both and knock them around.
 * Requires an active grapple hold (Grapple or Force Bind) to activate.
 *
 * State machine: idle → windup → recovery → cooldown → idle
 * The throw fires at the windup → recovery transition. Clinch DR
 * applies during windup and is removed when the throw fires.
 *
 * Throw physics (projectile arc, wall/enemy collision detection) are
 * delegated to an injected `throwTarget` interface — the SkillEffect
 * owns the decision logic, not the physics.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Dead Weight
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ThrowResult {
  /** How far the body actually traveled before stopping. */
  distanceTraveled: number;
  /** Whether the thrown body hit a wall. */
  hitWall: boolean;
  /** Whether the thrown body hit another enemy. */
  hitEnemy: boolean;
}

export interface DeadWeightDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
  readonly grapple: {
    isHoldActive(): boolean;
    cancelHold(): void;
  };
  readonly throwTarget: {
    launch(distanceTiles: number): ThrowResult;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DeadWeightConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseThrowDistanceTiles: number;
  readonly baseThrowDamage: number;
  readonly wallImpactBonusDamage: number;
  readonly collisionBonusDamage: number;
  readonly damageReduction: number;
}

const DEFAULT_CONFIG: DeadWeightConfig = {
  windupMs: 100,
  recoveryMs: 200,
  cooldownMs: 2000,              // long cooldown tier
  baseThrowDistanceTiles: 3,
  baseThrowDamage: 25,
  wallImpactBonusDamage: 0,      // boosted by Trebuchet
  collisionBonusDamage: 0,       // boosted by Chain Reaction
  damageReduction: 0.4,          // Clinch passive: DR during windup
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type DeadWeightPhase = 'idle' | 'windup' | 'recovery' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DeadWeightSkillEffect implements SkillEffect {
  readonly skillId = 'dead_weight';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: DeadWeightDeps;
  private readonly config: DeadWeightConfig;

  private _phase: DeadWeightPhase = 'idle';
  private _phaseTimer = 0;
  private _complete = false;

  // Throw state
  private _throwFired = false;
  private _throwResult: ThrowResult | null = null;
  private _resolvedDistance = 0;
  private _totalDamage = 0;
  private _wallHitDamage = 0;
  private _collisionDamage = 0;

  // DR state
  private _drApplied = false;

  constructor(deps: DeadWeightDeps, config: Partial<DeadWeightConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): DeadWeightPhase { return this._phase; }
  get throwFired(): boolean { return this._throwFired; }
  get throwResult(): ThrowResult | null { return this._throwResult; }
  get resolvedDistance(): number { return this._resolvedDistance; }
  get totalDamage(): number { return this._totalDamage; }
  get wallHitDamage(): number { return this._wallHitDamage; }
  get collisionDamage(): number { return this._collisionDamage; }
  get drApplied(): boolean { return this._drApplied; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._phase !== 'idle') return false;
    return this.deps.grapple.isHoldActive();
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Reset throw state
    this._complete = false;
    this._throwFired = false;
    this._throwResult = null;
    this._resolvedDistance = 0;
    this._totalDamage = 0;
    this._wallHitDamage = 0;
    this._collisionDamage = 0;
    this._drApplied = false;

    // Cancel the active grapple hold
    this.deps.grapple.cancelHold();

    // Apply Clinch DR during windup
    if (this.deps.resolver.hasEffect('grit', 'grappleDamageReduction')) {
      this.deps.statusEffects.apply('grit_force_bind_dr', this.config.windupMs, this.config.damageReduction, 'dead_weight');
      this._drApplied = true;
    }

    // Enter windup
    this._phase = 'windup';
    this._phaseTimer = this.config.windupMs;

    return true;
  }

  deactivate(): void {
    // Dead Weight has no toggle — once activated it runs to completion
  }

  isActive(): boolean {
    return this._phase === 'windup' || this._phase === 'recovery';
  }

  isComplete(): boolean {
    return this._complete;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    this.removeDr();
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- Internal ---

  private advancePhase(): void {
    switch (this._phase) {
      case 'windup':
        this.fireThrow();
        break;
      case 'recovery':
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
        this._complete = true;
        break;
      case 'cooldown':
        this._phase = 'idle';
        this._phaseTimer = 0;
        break;
    }
  }

  private fireThrow(): void {
    // Remove DR — throw is firing
    this.removeDr();

    // Resolve throw distance (base + Trebuchet bonus)
    const bonusDistance = this.deps.resolver.getAdditiveEffect('dead_weight', 'throwDistanceTiles');
    this._resolvedDistance = this.config.baseThrowDistanceTiles + bonusDistance;

    // Resolve impact damage bonuses
    const bonusWallDamage = this.deps.resolver.getAdditiveEffect('dead_weight', 'wallImpactDamage');
    const bonusCollisionDamage = this.deps.resolver.getAdditiveEffect('dead_weight', 'collateralBonusDamage');

    // Launch the thrown body
    this._throwResult = this.deps.throwTarget.launch(this._resolvedDistance);
    this._throwFired = true;

    // Compute damage based on outcome
    this._totalDamage = this.config.baseThrowDamage;

    if (this._throwResult.hitWall) {
      this._wallHitDamage = this.config.wallImpactBonusDamage + bonusWallDamage;
      this._totalDamage += this._wallHitDamage;
    }

    if (this._throwResult.hitEnemy) {
      this._collisionDamage = this.config.collisionBonusDamage + bonusCollisionDamage;
      this._totalDamage += this._collisionDamage;

      // Chain Reaction: stagger the struck enemy
      if (this.deps.resolver.hasEffect('dead_weight', 'collateralStaggerMs')) {
        const staggerMs = this.deps.resolver.getAdditiveEffect('dead_weight', 'collateralStaggerMs');
        if (staggerMs > 0) {
          this.deps.statusEffects.apply('stagger', staggerMs, 'dead_weight_upgrade_2');
        }
      }
    }

    // Enter recovery
    this._phase = 'recovery';
    this._phaseTimer = this.config.recoveryMs;
  }

  private removeDr(): void {
    if (this._drApplied) {
      this.deps.statusEffects.remove('grit_force_bind_dr', 'dead_weight');
      this._drApplied = false;
    }
  }
}
